import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';
import {
  QuickPlanPartnerDialog,
  QuickPlanPersonFields,
  quickPlanPersonSchema,
  useIntakeFormQuickPlan,
  type QuickPlanPersonValues,
} from '@/features/client-quick-plan';

import { AlertCircle, ArrowRight, UserPen, UserPlus, X } from 'lucide-react';

import { useEffect, useState } from 'react';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

import { format } from 'date-fns';

interface IntakeFormQuickPlanFormProps {
  advisorId: string;
  onDone: (data: ShortFormIntakeResponse) => void;
  onPartnerDialogChange?: (open: boolean) => void;
  /** Starts with the questionnaire tick box ticked, for a dialog opened to send the form. */
  initialSendIntakeForm?: boolean;
}

/**
 * The New Case Quick Start form: the basics that make a plan, and a tick box to also email the
 * client the medical questionnaire. See `use-intake-form-quick-plan.ts` for why it is one form.
 */
export function IntakeFormQuickPlanForm({
  advisorId,
  onDone,
  onPartnerDialogChange,
  initialSendIntakeForm = false,
}: IntakeFormQuickPlanFormProps) {
  const { flags } = useFeatureFlags();
  const [showPartnerPanel, setShowPartnerPanel] = useState(false);
  const {
    form,
    hasPartner,
    canAddPartner,
    partnerLabel,
    sendIntakeForm,
    handleSendIntakeFormChange,
    handleTogglePartner,
    handleMaritalStatusChange,
    handlePartnerMaritalStatusChange,
    onSubmit,
    isValid,
    isPending,
  } = useIntakeFormQuickPlan({
    advisorId,
    onSuccess: data => onDone(data),
    initialSendIntakeForm,
  });

  const showPartnerOption = flags.coupleQuickPlan && canAddPartner;

  const partnerValues = form.watch('partner') as QuickPlanPersonValues | undefined;
  // The partner's email is asked on this form, beside the client's, and not in the partner
  // dialog. It is only needed once the questionnaire is being sent, and the tick box can be
  // ticked after the dialog has closed: asked in there, it became a required field nobody could
  // see, with Submit disabled and nothing saying why.
  const isPartnerValid = partnerValues
    ? quickPlanPersonSchema.safeParse(partnerValues).success
    : false;

  useEffect(() => {
    onPartnerDialogChange?.(showPartnerPanel);
  }, [showPartnerPanel, onPartnerDialogChange]);

  function handleAddPartner() {
    handleTogglePartner(true);
    setShowPartnerPanel(true);
  }

  function handleRemovePartner() {
    handleTogglePartner(false);
    setShowPartnerPanel(false);
  }

  function handlePartnerDone() {
    setShowPartnerPanel(false);
  }

  function handlePartnerCancel() {
    if (isPartnerValid) {
      setShowPartnerPanel(false);
    } else {
      handleTogglePartner(false);
      setShowPartnerPanel(false);
    }
  }

  function handlePartnerDialogOpenChange(open: boolean) {
    if (!open) {
      handlePartnerCancel();
    } else {
      setShowPartnerPanel(true);
    }
  }

  const capitalizedLabel = partnerLabel.charAt(0).toUpperCase() + partnerLabel.slice(1);

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="space-y-6"
      >
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Alert
                  variant="info"
                  className="gap-x-2! cursor-help p-3"
                >
                  <AlertCircle />
                  <AlertTitle>Quick plans use limited data</AlertTitle>
                </Alert>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              For a full plan with personalized projections, have your client complete the intake
              form.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <fieldset disabled={isPending}>
          <div className="space-y-6">
            <QuickPlanPersonFields
              control={form.control}
              onMaritalStatusChange={handleMaritalStatusChange}
            />

            <div className="flex items-start gap-2">
              <Checkbox
                id="quick-start-send-intake-form"
                checked={sendIntakeForm}
                onCheckedChange={checked => handleSendIntakeFormChange(checked === true)}
              />
              <label
                htmlFor="quick-start-send-intake-form"
                className="cursor-pointer text-sm leading-5"
              >
                Also email the medical questionnaire to the client
              </label>
            </div>

            {sendIntakeForm && (
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Client email <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="jane@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {sendIntakeForm && hasPartner && (
              <FormField
                control={form.control}
                name="partnerEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {capitalizedLabel} email <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="partner@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </fieldset>

        {showPartnerOption && hasPartner && partnerValues && (
          <div className="border-border bg-muted flex items-start justify-between rounded-lg border p-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-0.5">
              <p className="text-sm font-medium">
                {capitalizedLabel}: {partnerValues.firstName} {partnerValues.lastName}
              </p>
              <p className="text-muted-foreground text-sm">
                {partnerValues.birthDate ? format(partnerValues.birthDate, 'MMM d, yyyy') : ''} ·{' '}
                {partnerValues.sex === 'male' ? 'Male' : 'Female'} · {partnerValues.zipCode}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              <Tooltip>
                <TooltipTrigger type="button">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 rounded-sm"
                    onClick={() => setShowPartnerPanel(true)}
                  >
                    <UserPen />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="px-2">Edit</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger type="button">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive size-7 rounded-sm"
                    onClick={handleRemovePartner}
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="px-2">Remove</TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {showPartnerOption && !hasPartner && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleAddPartner}
            >
              <UserPlus />
              Add {partnerLabel}
            </Button>
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={!isValid || isPending}
          >
            {isPending
              ? sendIntakeForm
                ? 'Sending...'
                : 'Working...'
              : sendIntakeForm
                ? 'Create plan & send intake form'
                : 'Generate quick plan'}
            {!isPending && <ArrowRight />}
          </Button>
        </div>

        <QuickPlanPartnerDialog
          open={showPartnerPanel}
          onOpenChange={handlePartnerDialogOpenChange}
          form={form}
          partnerLabel={partnerLabel}
          onMaritalStatusChange={handlePartnerMaritalStatusChange}
          onDone={handlePartnerDone}
          onCancel={handlePartnerCancel}
          isPartnerValid={isPartnerValid}
        />
      </form>
    </Form>
  );
}
